/**
 * Type Guards and Type Safety Utilities
 *
 * This module provides type guards and utility functions to improve type safety
 * throughout the application, replacing type assertions with proper runtime checks.
 */

import type { JWTPayload, JWTToken, User } from "@/types/auth"
import type {
  ApiResult,
  AsyncState,
  FieldState,
  ModalState,
} from "@/types/common"
import type { AxiosError } from "axios"

// AppError class for error type guards (will be defined in @/utils/errors if it exists)
class AppErrorClass extends Error {
  constructor(message: string) {
    super(message)
    this.name = "AppError"
  }
}

/**
 * Type guard to check if a value is a non-null object
 */
export const isObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

/**
 * Type guard to check if a value is a string
 */
export const isString = (value: unknown): value is string => {
  return typeof value === "string"
}

/**
 * Type guard to check if a value is a number
 */
export const isNumber = (value: unknown): value is number => {
  return typeof value === "number" && !Number.isNaN(value)
}

/**
 * Type guard to check if a value is an array
 */
export const isArray = (value: unknown): value is unknown[] => {
  return Array.isArray(value)
}

/**
 * Type guard to check if a value is a valid User object
 */
export const isUser = (value: unknown): value is User => {
  if (!isObject(value)) return false

  const obj = value as Record<string, unknown>

  return (
    isString(obj.id) &&
    isString(obj.email) &&
    isString(obj.full_name) &&
    typeof obj.is_active === "boolean" &&
    typeof obj.email_verified === "boolean" &&
    isString(obj.created_at) &&
    isString(obj.updated_at) &&
    (obj.email_verified_at === null || isString(obj.email_verified_at)) &&
    (obj.last_login === null || isString(obj.last_login))
  )
}

/**
 * Type guard to check if a value is a valid JWT Token response
 */
export const isJWTToken = (value: unknown): value is JWTToken => {
  if (!isObject(value)) return false

  const obj = value as Record<string, unknown>

  return (
    isString(obj.access_token) &&
    isString(obj.token_type) &&
    isNumber(obj.expires_in) &&
    (obj.refresh_token === null ||
      obj.refresh_token === undefined ||
      isString(obj.refresh_token)) &&
    (obj.scopes === undefined || isArray(obj.scopes))
  )
}

/**
 * Type guard to check if a value is a valid JWT Payload
 */
export const isJWTPayload = (value: unknown): value is JWTPayload => {
  if (!isObject(value)) return false

  const obj = value as Record<string, unknown>

  return (
    isString(obj.sub) &&
    isString(obj.email) &&
    isNumber(obj.exp) &&
    isNumber(obj.iat) &&
    isArray(obj.scopes) &&
    (obj.tenant_id === undefined || isString(obj.tenant_id))
  )
}

/**
 * Type guard to check if an error has a response property (HTTP error)
 */
export const isHttpError = (
  error: unknown,
): error is { response: { status: number; data: unknown } } => {
  return (
    isObject(error) &&
    isObject((error as any).response) &&
    isNumber((error as any).response.status)
  )
}

/**
 * Type guard to check if an error is a network error
 */
export const isNetworkError = (
  error: unknown,
): error is { code: string; message: string } => {
  return (
    isObject(error) &&
    isString((error as any).code) &&
    ((error as any).code === "NETWORK_ERROR" ||
      (error as any).message?.includes("Network"))
  )
}

/**
 * Type guard for API response structure
 */
export interface ApiResponse<T = unknown> {
  status: string
  data?: T
  error?: {
    message: string
    code?: string
    details?: Record<string, unknown>
  }
  meta?: Record<string, unknown>
}

export const isApiResponse = <T = unknown>(
  value: unknown,
): value is ApiResponse<T> => {
  if (!isObject(value)) return false

  const obj = value as Record<string, unknown>

  return (
    isString(obj.status) &&
    (obj.data === undefined || obj.data !== null) &&
    (obj.error === undefined ||
      (isObject(obj.error) && isString((obj.error as any).message))) &&
    (obj.meta === undefined || isObject(obj.meta))
  )
}

/**
 * Type guards for common discriminated unions
 */

export const isAsyncState = <T>(value: unknown): value is AsyncState<T> => {
  if (!isObject(value)) return false

  const obj = value as Record<string, unknown>

  if (!isString(obj.status)) return false

  switch (obj.status) {
    case "idle":
    case "loading":
      return true
    case "success":
      return obj.data !== undefined
    case "error":
      return obj.error !== undefined
    default:
      return false
  }
}

export const isApiResult = <T>(value: unknown): value is ApiResult<T> => {
  if (!isObject(value)) return false

  const obj = value as Record<string, unknown>

  if (typeof obj.success !== "boolean") return false

  if (obj.success) {
    return obj.data !== undefined
  }
  return obj.error !== undefined
}

export const isFieldState = <T>(value: unknown): value is FieldState<T> => {
  if (!isObject(value)) return false

  const obj = value as Record<string, unknown>

  if (!isString(obj.status)) return false
  if (obj.value === undefined) return false

  switch (obj.status) {
    case "pristine":
    case "dirty":
    case "validating":
    case "valid":
      return true
    case "invalid":
      return isString(obj.error)
    default:
      return false
  }
}

export const isModalState = <T>(value: unknown): value is ModalState<T> => {
  if (!isObject(value)) return false

  const obj = value as Record<string, unknown>

  return typeof obj.isOpen === "boolean"
}

/**
 * Safe JSON parser with type guard
 */
export const safeParse = <T>(
  json: string,
  typeGuard: (value: unknown) => value is T,
): T | null => {
  try {
    const parsed = JSON.parse(json)
    return typeGuard(parsed) ? parsed : null
  } catch {
    return null
  }
}

/**
 * Extract property safely with type guard
 */
export const extractProperty = <T>(
  obj: unknown,
  key: string,
  typeGuard: (value: unknown) => value is T,
): T | null => {
  if (!isObject(obj)) return null

  const value = (obj as Record<string, unknown>)[key]
  return typeGuard(value) ? value : null
}

/**
 * Assert that a value matches a type guard, throwing if not
 */
export const assertType = <T>(
  value: unknown,
  typeGuard: (value: unknown) => value is T,
  errorMessage?: string,
): T => {
  if (typeGuard(value)) {
    return value
  }
  throw new Error(errorMessage || "Type assertion failed")
}

/**
 * Create a type guard for arrays of a specific type
 */
export const arrayOf = <T>(itemGuard: (value: unknown) => value is T) => {
  return (value: unknown): value is T[] => {
    return isArray(value) && value.every(itemGuard)
  }
}

/**
 * Create a type guard for optional properties
 */
export const optional = <T>(typeGuard: (value: unknown) => value is T) => {
  return (value: unknown): value is T | undefined => {
    return value === undefined || typeGuard(value)
  }
}

/**
 * ERROR HANDLING TYPE GUARDS
 * These type guards are critical for replacing 'any' types in error handlers
 * Per ADR-043: Use these instead of catch (error: unknown)
 */

/**
 * Type guard for AppErrorClass instances
 */
export const isAppError = (error: unknown): error is AppErrorClass => {
  return error instanceof AppErrorClass
}

/**
 * Type guard for standard Error instances
 */
export const isError = (error: unknown): error is Error => {
  return error instanceof Error
}

/**
 * Type guard for objects with a message property
 */
export const isErrorWithMessage = (
  error: unknown,
): error is { message: string } => {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  )
}

/**
 * Type guard for Axios errors
 */
export const isAxiosError = (error: unknown): error is AxiosError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "isAxiosError" in error &&
    (error as { isAxiosError: unknown }).isAxiosError === true
  )
}

/**
 * Extract error message from unknown error type
 *
 * Use this instead of error?.message for type-safe error handling
 * Per ADR-043: Pattern for onError callbacks
 *
 * @param error - Unknown error from catch block or callback
 * @param fallback - Fallback message if error has no message
 * @returns Safe error message string
 *
 * @example
 * ```typescript
 * // ✅ CORRECT
 * onError: (error: unknown) => {
 *   const message = getErrorMessage(error, 'Failed to delete user')
 *   showErrorToast(message)
 * }
 * ```
 */
export const getErrorMessage = (
  error: unknown,
  fallback = "An unexpected error occurred",
): string => {
  if (isAppError(error)) {
    return error.message
  }

  if (isError(error)) {
    return error.message
  }

  if (isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | undefined
    return (
      (typeof data?.message === "string" ? data.message : undefined) ||
      error.message
    )
  }

  if (isErrorWithMessage(error)) {
    return error.message
  }

  if (isString(error)) {
    return error
  }

  return fallback
}
